import { useEffect, useState } from "react";
import styled from "styled-components";
import React from 'react';
import Disclaimer from "@/app/ui/Disclaimer"
import { getTrackAnalysis } from '@/app/utils/request/requestList';
import { useLanguage } from '@/app/utils/LanguageContext';
import { useMyContext } from '@/app/utils/MyContext';
import VideoPlayerIcon from "@/app/ui/Common/SvgComponent/VideoPlayerIcon";
import { ValidVideo, VideoSource } from "@/app/utils/class/common/video";
import { generateVideoSource, generateVideoUrl, validateVideo } from "@/app/utils/Video";
import VideoPlayer from "@/app/ui/Common/VideoPlayer";
import { useMediaQuery } from "@mui/material";
import { generateAdobeDataRaceNo, getSearchParams, setRacesDetailLoading, theme } from "@/app/utils/Common";
import NoInformation from "@/app/ui/Common/NoInformation";
import { text_size } from "@/app/utils/MixinSize";
import ExpertProfile from "@/app/ui/Common/ExpertProfile"
import { VideoWAData } from "@/app/ui/Common/VideoPlayer/player";

const DivContainer =  styled('div')<{ $assetPrefix?: string }>(({ $assetPrefix }) => `
    border-radius: 0 0 4px 4px;
    background-image: url(${$assetPrefix}/text_bg.png);
    background-repeat: repeat-y;
`);

const DivContainerM = styled.div`
    background-color: #FFFFFF;
    border-radius: 0 0 4px 4px;
    padding:12px;
    > div + div {
        margin-top: 20px;
    }
`;

const TrackAnalysisHeader = styled.div`
  width: 100%;
  height: 36px;
  padding-left: 12px;
  ${text_size(15)}
  line-height: 36px;
  font-weight: 500;
  background-color: #F4F4F4;
`;

const DivTrackAnalysiContent = styled.div`
    padding: 0 16px 0 12px;
    max-width: 657.44px;
    border-right: 1px solid #B4B4B4;
`;

const DivTrackAndVideo = styled.div`
    width: 270px;
    float: left;
    margin: 0 12px 12px 12px;
    padding-top: 8px;
`;

const DivVideo = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor:pointer;
    border-radius: 4px 4px 4px 4px;
`;

const DivVideoM = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 335px;
    width: 100%;
    margin: 0 auto;
    padding: 12px;
`;

const DivVideoPlayIcon = styled.div`
    position: absolute;
    text-align: center;
    vertical-align: text-bottom;

    svg {
        width: 38px;
        height: 38px;
    }
`;

const ImgTrack = styled.img`
    width: 100%;
    display: block;
    margin: 0 auto;
    border-radius: 4px 4px 4px 4px;
`;

const ImgVideo = styled.img`
    width: 100%;
    display: block;
    margin: 0 auto;
    border-radius: 4px 4px 0px 0px;
`;

const DivCourseOverview = styled.div`
    background: #333333;
    border-radius: 0px 0px 4px 4px;
    font-weight: 500;
    ${text_size(15)}
    line-height: 20px;
    max-height: 48px;
    align-items: center;
    color: #FFFFFF;
    padding: 4px 12px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ImgTrackM = styled.img`
    max-width: 256px;
    width: 100%;
    display: block;
    margin: 0 auto;
    border-radius: 4px 4px 4px 4px;
`;
const DivTitle = styled.div`
    ${text_size(15)}
    font-weight: 700;
    line-height: 17px;
    padding-bottom: 4px;
    font-family: "Noto Sans TC" !important;
`;
const DivContent = styled.div`
    ${text_size(13)}
    font-weight: 500;
    margin-bottom: 24px;
`;

const DivTopSolid = styled.div`
    border-top: 1px solid #B4B4B4;
    clear: both;
    margin: 0 12px;
    max-width: 935px;
`;

const TopContentWrapper = styled.div`
    max-width: 959px;
    display: flex;
    padding: 20px 0 16px 0;
`;
const BottomContentWrapper = styled.div` 
    max-width: 943px;
    padding: 16px 12px 20px 12px;
`;

interface propType {
    setRacesDetailLoading: (loading: boolean) => void;
    type?: string;
}

const TrackAnalysis: React.FC<propType> = (props: propType) => {
    const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
    const { state: lang } = useLanguage();
    const { t } = lang;
    const { state: myContextState, dispatch: myContextDispatch } = useMyContext();
    const [trackAnalysisData, setTrackAnalysisData] = useState<any>(null);
    const [expertName, setExpertName] = useState<string>('');
    const [isShowVideoPlay, setIsShowVideoPlay] = useState(false);
    const [videoSources, setVideoSources] = useState([] as VideoSource[]);
    const [WAData, setWAData] = useState<VideoWAData | undefined>(undefined);
    const { b_cid } = getSearchParams(new URLSearchParams(window.location.search));

    const handleVideoClose = () => {
        setIsShowVideoPlay(false);
        setWAData(undefined);
        setVideoSources([])
    }

    const openVideoPlayer = (validVideo: ValidVideo, url: any, waContentTitle?: string) => {
        if (!validVideo.hasVideo()) return;
        setWAData({
            raceNo: generateAdobeDataRaceNo(myContextState.selectedMeetingDate, myContextState.selectedMeeting?.meetingVenueCode, myContextState.selectedRaceNo.valueOf()),
            contentTitle: waContentTitle,
        });
        setVideoSources(generateVideoSource(url.sd, url.hd));
        setIsShowVideoPlay(true);
    }

    useEffect(() => {
        if (myContextState.isLoadingRaces) return;
        if (!myContextState.selectedMeeting?.displayName) {
            setRacesDetailLoading(props.setRacesDetailLoading)
            return
        }
        getTrackAnalysis(lang.langCode, myContextState.season, myContextState.selectedMeeting?.displayName)
            .then(async (res: any) => {
                if (res?.item) {
                    const videoUrl = generateVideoUrl(res?.item?.videoLink?.value || "");
                    const videoValidation = await validateVideo(videoUrl);
                    const trackAnalysisData = { ...res?.item, videoUrl: videoUrl, videoValidation: videoValidation };
                    setTrackAnalysisData(trackAnalysisData);
                    const expertName = res?.item?.expertSelection?.value || '';

                    if (expertName !== '') {
                        setExpertName(expertName);
                    }
                } else {
                    setTrackAnalysisData(null);
                }

            })
            .catch((e: any) => {
                console.log(e);
            }).finally(() => {
                setRacesDetailLoading(props.setRacesDetailLoading)
            });
    }, [myContextState.selectedMeeting, myContextState.isLoadingRaces]);

    if (!trackAnalysisData)
        return (<>
            <TrackAnalysisHeader id="TrackAnalysis_Header">
                {t('track_analysis')}
            </TrackAnalysisHeader>
            <NoInformation></NoInformation>
        </>)

    const showTrackVideo = () => trackAnalysisData?.videoThumbnail?.src && trackAnalysisData?.videoLink?.value && trackAnalysisData?.videoValidation?.hasVideo();

    return (
        <>
            {props.type === "pc" ?
                (
                    <DivContainer $assetPrefix={myContextState.assetPrefix}>
                        <TrackAnalysisHeader id="TrackAnalysis_Header">
                            {t('track_analysis')}
                        </TrackAnalysisHeader>
                        {(trackAnalysisData?.title?.value && trackAnalysisData?.description?.value) ?
                            <TopContentWrapper>
                                <DivTrackAnalysiContent id="trackAnalysis_content_pc">
                                    <DivTitle id="trackAnalysisContent_title_pc">{trackAnalysisData?.title?.value}</DivTitle>
                                    <DivContent id="trackAnalysisContent_content_pc" dangerouslySetInnerHTML={{ __html: trackAnalysisData?.description?.value?.replaceAll("\r\n", "<br/>") ?? '' }}></DivContent>
                                    {expertName !== '' ? <ExpertProfile type="pc" expertName={expertName}></ExpertProfile> : <></>}
                                </DivTrackAnalysiContent>

                                <DivTrackAndVideo>
                                    {trackAnalysisData?.trackMap?.targetItem?.trackImage?.src ? <ImgTrack id="trackAnalysis_img_pc" src={trackAnalysisData?.trackMap?.targetItem?.trackImage?.src || ''} alt={trackAnalysisData?.trackMap?.targetItem?.trackImage?.alt || ''} /> : <></>} 
                                    {showTrackVideo() ?
                                        <DivVideo onClick={() => openVideoPlayer(trackAnalysisData?.videoValidation, trackAnalysisData?.videoUrl, trackAnalysisData?.videoTitle?.value)} >
                                            <DivVideoPlayIcon id="trackAnalysis_VideoPlayerIcon_pc"><VideoPlayerIcon /></DivVideoPlayIcon>
                                            <div style={{ width: '270px' }}>
                                                <ImgVideo src={trackAnalysisData?.videoThumbnail?.src} alt="" />
                                                {trackAnalysisData?.videoTitle?.value && <DivCourseOverview id="trackAnalysis_VideoPlayer_description_pc">{trackAnalysisData?.videoTitle?.value}</DivCourseOverview>}
                                            </div>
                                        </DivVideo> : <></>
                                    }
                                </DivTrackAndVideo>
                            </TopContentWrapper>
                            : <></>
                        }
                        <DivTopSolid></DivTopSolid>
                        <BottomContentWrapper>
                            <Disclaimer></Disclaimer>
                        </BottomContentWrapper>
                    </DivContainer>
                ) : (
                    <DivContainerM>
                        {(trackAnalysisData?.title?.value && trackAnalysisData?.description?.value) ?
                            <>
                                <DivTitle id="trackAnalysis_title_mobile">{trackAnalysisData?.title?.value}</DivTitle>
                                {trackAnalysisData?.trackMap?.targetItem?.trackImage?.src ? <ImgTrackM id="trackAnalysis_img_mobile" src={trackAnalysisData?.trackMap?.targetItem?.trackImage?.src || ''} alt={trackAnalysisData?.trackMap?.targetItem?.trackImage?.alt || ''} /> : <></>}
                                <DivContent id="trackAnalysis_content_mobile" dangerouslySetInnerHTML={{ __html: trackAnalysisData?.description?.value?.replaceAll("\r\n", "<br/>") ?? '' }}></DivContent>
                                {showTrackVideo() ?
                                    <DivVideoM onClick={() => openVideoPlayer(trackAnalysisData?.videoValidation, trackAnalysisData?.videoUrl)} >
                                        <DivVideoPlayIcon id="trackAnalysis_VideoPlayerIcon_mobile"><VideoPlayerIcon /></DivVideoPlayIcon>
                                        <div>
                                            <ImgVideo src={trackAnalysisData?.videoThumbnail?.src} alt="" />
                                            {trackAnalysisData?.videoTitle?.value && <DivCourseOverview id="trackAnalysis_VideoPlayer_description_mobile">{trackAnalysisData?.videoTitle?.value}</DivCourseOverview>}
                                        </div>
                                    </DivVideoM> : <></>
                                }
                            </>
                            : <></>
                        }
                        {expertName !== '' ? <ExpertProfile type="mobile" expertName={expertName}></ExpertProfile> : <></>}
                        <Disclaimer></Disclaimer>
                    </DivContainerM>
                )
            }
            <VideoPlayer WAData={WAData} sources={videoSources} open={isShowVideoPlay} isMobile={isMobile} handleClose={handleVideoClose} width={isMobile ? undefined : '60%'} height={isMobile ? undefined : '60%'} showCurrentTooltip showHoveredTooltip autoPlay />
        </>
    )
}

export default TrackAnalysis; 